import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Textarea, Select, Field } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { MediaField } from '@/components/forms/MediaField';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { Department, TeamRow } from '@/lib/admin-queries';

// Zod's z.enum() requires a tuple, not an array. Keep this const-tuple.
const DEPARTMENTS = ['leadership', 'engineering', 'design', 'product', 'operations'] as const satisfies readonly Department[];
const STATUSES = ['draft', 'published', 'archived'] as const;

const schema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string().min(2),
  role: z.string().min(2),
  departments: z.array(z.enum(DEPARTMENTS)).min(1, 'Pick at least one department'),
  status: z.enum(STATUSES),
});

interface Props {
  value: TeamRow;
  submitting: boolean;
  onSubmit: (v: TeamRow) => void | Promise<void>;
  onCancel: () => void;
}

export function TeamEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const [v, setV] = useState<TeamRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [skillInput, setSkillInput] = useState('');

  function patch<K extends keyof TeamRow>(key: K, val: TeamRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onNameBlur() {
    if (!v.slug) patch('slug', slugify(v.name));
  }

  function toggleDepartment(d: Department) {
    const has = v.departments.includes(d);
    patch('departments', has ? v.departments.filter((x) => x !== d) : [...v.departments, d]);
  }

  function addSkill() {
    const s = skillInput.trim();
    if (!s) return;
    if (!v.skills.includes(s)) patch('skills', [...v.skills, s]);
    setSkillInput('');
  }

  function removeSkill(s: string) {
    patch('skills', v.skills.filter((x) => x !== s));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse(v);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="team-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="name" label="Full name" required error={errors.name}>
          {({ id, invalid }) => (
            <Input id={id} required invalid={invalid} value={v.name} onChange={(e) => patch('name', e.target.value)} onBlur={onNameBlur} />
          )}
        </Field>
        <Field id="role" label="Role" required error={errors.role}>
          {({ id, invalid }) => (
            <Input id={id} required invalid={invalid} value={v.role} onChange={(e) => patch('role', e.target.value)} />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field id="slug" label="Slug" required error={errors.slug}>
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.slug}
              onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
          )}
        </Field>
        <Field id="departments" label="Departments" required error={errors.departments} hint="Pick one or more. Shown on the team card.">
          <div id="departments" className="flex flex-wrap gap-1.5">
            {DEPARTMENTS.map((d) => {
              const active = v.departments.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDepartment(d)}
                  className={
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ' +
                    (active
                      ? 'border-gold-500/40 bg-gold-500/10 text-gold-500'
                      : 'border-border bg-bg-elevated text-fg-muted hover:border-gold-500/40 hover:text-fg')
                  }
                  aria-pressed={active}
                >
                  {d}
                  {active && <span aria-hidden>×</span>}
                </button>
              );
            })}
          </div>
        </Field>
        <Field id="status" label="Publish" required>
          {({ id }) => (
            <Select id={id} value={v.status} onChange={(e) => patch('status', e.target.value as TeamRow['status'])}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field id="bio" label="Short bio" hint="Shown on the team card. Markdown ok.">
        {({ id }) => (
          <Textarea id={id} rows={3} value={v.bio ?? ''} onChange={(e) => patch('bio', e.target.value)} />
        )}
      </Field>

      <Field id="bio_md" label="Long bio" hint="Optional · shown on a detail page or hover card.">
        <MarkdownField value={v.bio_md ?? ''} onChange={(s) => patch('bio_md', s)} rows={10} />
      </Field>

      <Field id="portrait" label="Portrait" hint="1:1 image · shown on the team card.">
        <MediaField value={v.portrait_media_id} onChange={(m) => patch('portrait_media_id', m)} bucket="public" />
      </Field>

      <div className="space-y-2">
        <label className="text-xs font-medium text-fg-muted">Skills</label>
        <div className="flex flex-wrap gap-1.5">
          {v.skills.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => removeSkill(s)}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg hover:border-danger/40 hover:text-danger"
            >
              {s} <span aria-hidden>×</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add skill (e.g. TypeScript)"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSkill();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={addSkill}>
            Add
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field id="linkedin_url" label="LinkedIn URL">
          {({ id }) => (
            <Input id={id} type="url" value={v.linkedin_url ?? ''} onChange={(e) => patch('linkedin_url', e.target.value)} />
          )}
        </Field>
        <Field id="github_url" label="GitHub URL">
          {({ id }) => (
            <Input id={id} type="url" value={v.github_url ?? ''} onChange={(e) => patch('github_url', e.target.value)} />
          )}
        </Field>
        <Field id="email" label="Email">
          {({ id }) => (
            <Input id={id} type="email" value={v.email ?? ''} onChange={(e) => patch('email', e.target.value)} />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="display_order" label="Display order">
          {({ id }) => (
            <Input id={id} type="number" value={v.display_order} onChange={(e) => patch('display_order', Number(e.target.value))} />
          )}
        </Field>
        <label className="mt-6 flex items-center gap-2 text-sm">
          <Switch checked={v.is_active} onCheckedChange={(c) => patch('is_active', !!c)} id="is_active" />
          <span>Show on public team page</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="team-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Add member'}
        </Button>
      </div>
    </form>
  );
}